import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  ArrowLeft, Mail, Lock, Server, Save, Trash2, Send, 
  MessageCircle, CheckCircle, AlertCircle, Info, Eye, EyeOff
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSettingsPage = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  // Email form
  const [smtpEmail, setSmtpEmail] = useState("");
  const [smtpPassword, setSmtpPassword] = useState("");
  const [smtpServer, setSmtpServer] = useState("smtp.gmail.com");
  const [smtpPort, setSmtpPort] = useState(587);
  const [testEmail, setTestEmail] = useState("");

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`);
      setSettings(response.data);
      if (response.data.smtp_email) {
        setSmtpEmail(response.data.smtp_email);
        setSmtpServer(response.data.smtp_server || "smtp.gmail.com");
        setSmtpPort(response.data.smtp_port || 587);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error("Erreur lors du chargement des paramètres");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEmail = async (e) => {
    e.preventDefault();
    if (!smtpEmail || !smtpPassword) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/admin/settings/email`, {
        smtp_email: smtpEmail,
        smtp_password: smtpPassword,
        smtp_server: smtpServer,
        smtp_port: smtpPort
      });
      toast.success("Configuration email sauvegardée !");
      setSmtpPassword("");
      fetchSettings();
    } catch (error) {
      console.error("Error saving email:", error);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEmail = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer la configuration email ?")) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/settings/email`);
      toast.success("Configuration email supprimée");
      setSmtpEmail("");
      setSmtpPassword("");
      fetchSettings();
    } catch (error) {
      console.error("Error deleting email:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      toast.error("Entrez une adresse email de test");
      return;
    }

    try {
      await axios.post(`${API}/admin/settings/email/test?to_email=${testEmail}`);
      toast.success(`Email de test envoyé à ${testEmail}`);
    } catch (error) {
      console.error("Error testing email:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi du test");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="animate-pulse text-[#64748B]">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="admin-settings-page">
      {/* Header */}
      <nav className="navbar px-4 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/director")}
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Retour
          </Button>
          <span className="navbar-logo hidden sm:block">SophIA</span>
          <Badge className="ml-2 bg-[#D4AF37] text-[#0F172A]">Paramètres</Badge>
        </div>
      </nav>

      <main className="px-4 md:px-8 py-6 max-w-4xl mx-auto page-enter">
        <h1 className="text-2xl md:text-3xl text-[#1A365D] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
          Paramètres Administration
        </h1>

        {/* Email Configuration */}
        <Card className="card-sjh mb-6" data-testid="email-config-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#1A365D]" />
                <CardTitle className="text-lg text-[#1A365D]">Configuration Email (SMTP)</CardTitle>
              </div>
              {settings?.email_configured ? (
                <Badge className="bg-[#22C55E]">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Configuré
                </Badge>
              ) : (
                <Badge variant="outline" className="border-[#EAB308] text-[#EAB308]">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  Non configuré
                </Badge>
              )}
            </div>
            <CardDescription>
              Configurez un email SMTP pour recevoir des notifications (alertes élèves inactifs, incidents de modération)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="p-3 bg-[#FEF3C7] rounded-lg border border-[#F59E0B] mb-4">
              <p className="text-sm text-[#92400E] flex items-start gap-2">
                <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>Recommandation :</strong> Utilisez une adresse email dédiée pour l'application 
                  (ex: sjh-philoai@gmail.com). Pour Gmail, vous devrez activer les "mots de passe d'application" 
                  dans les paramètres de sécurité de votre compte Google.
                </span>
              </p>
            </div>

            <form onSubmit={handleSaveEmail} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="smtpEmail" className="text-[#0F172A]">Adresse email SMTP</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                    <Input 
                      id="smtpEmail"
                      type="email"
                      value={smtpEmail}
                      onChange={(e) => setSmtpEmail(e.target.value)}
                      placeholder="notifications@sjh.fr"
                      className="pl-10 border-[#E2E8F0] focus:border-[#1A365D]"
                      data-testid="smtp-email-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="smtpPassword" className="text-[#0F172A]">
                    Mot de passe {settings?.email_configured && "(laisser vide pour conserver)"}
                  </Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                    <Input 
                      id="smtpPassword"
                      type={showPassword ? "text" : "password"}
                      value={smtpPassword}
                      onChange={(e) => setSmtpPassword(e.target.value)}
                      placeholder={settings?.email_configured ? "••••••••" : "Mot de passe ou App Password"}
                      className="pl-10 pr-10 border-[#E2E8F0] focus:border-[#1A365D]"
                      data-testid="smtp-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B]"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="smtpServer" className="text-[#0F172A]">Serveur SMTP</Label>
                  <div className="relative mt-1">
                    <Server className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                    <Input 
                      id="smtpServer"
                      value={smtpServer}
                      onChange={(e) => setSmtpServer(e.target.value)}
                      placeholder="smtp.gmail.com"
                      className="pl-10 border-[#E2E8F0] focus:border-[#1A365D]"
                      data-testid="smtp-server-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="smtpPort" className="text-[#0F172A]">Port SMTP</Label>
                  <Input 
                    id="smtpPort"
                    type="number"
                    value={smtpPort}
                    onChange={(e) => setSmtpPort(parseInt(e.target.value))}
                    placeholder="587"
                    className="mt-1 border-[#E2E8F0] focus:border-[#1A365D]"
                    data-testid="smtp-port-input"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <Button 
                  type="submit"
                  className="bg-[#1A365D] hover:bg-[#132745]"
                  disabled={saving}
                  data-testid="save-email-button"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Sauvegarde..." : "Sauvegarder"}
                </Button>
                
                {settings?.email_configured && (
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={handleDeleteEmail}
                    className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444] hover:text-white"
                    data-testid="delete-email-button"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer
                  </Button>
                )}
              </div>
            </form>

            {settings?.email_configured && (
              <>
                <Separator className="my-4" />
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <Label htmlFor="testEmail" className="text-[#0F172A]">Tester la configuration</Label>
                    <Input 
                      id="testEmail"
                      type="email"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="votre-email@example.com"
                      className="mt-1 border-[#E2E8F0] focus:border-[#1A365D]"
                      data-testid="test-email-input"
                    />
                  </div>
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={handleTestEmail}
                    className="border-[#22C55E] text-[#22C55E] hover:bg-[#22C55E] hover:text-white"
                    data-testid="send-test-button"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Envoyer un test
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* WhatsApp Integration Info */}
        <Card className="card-sjh" data-testid="whatsapp-info-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-[#25D366]" />
                <CardTitle className="text-lg text-[#1A365D]">Intégration WhatsApp Business</CardTitle>
              </div>
              <Badge variant="outline" className="border-[#64748B] text-[#64748B]">
                Bientôt disponible
              </Badge>
            </div>
            <CardDescription>
              Créez des groupes WhatsApp par classe avec les philosophes virtuels
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="p-4 bg-[#F0FDF4] rounded-lg border border-[#22C55E]/30">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-medium text-[#0F172A] mb-1">
                    Fonctionnalité en préparation
                  </p>
                  <p className="text-sm text-[#64748B] mb-3">
                    L'intégration WhatsApp Business permettra de créer des groupes de discussion 
                    par classe où les philosophes virtuels pourront intervenir automatiquement.
                  </p>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-[#64748B]">Numéro réservé :</span>
                    <Badge className="bg-[#25D366]">
                      {settings?.whatsapp_number || "0644692901"}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-[#F8F9FA] rounded-lg">
              <p className="text-sm text-[#64748B]">
                <strong>Fonctionnalités prévues :</strong>
              </p>
              <ul className="mt-2 text-sm text-[#64748B] space-y-1">
                <li>• Création automatique de groupes par classe</li>
                <li>• Les philosophes répondent aux questions des élèves</li>
                <li>• Notifications push pour les rappels</li>
                <li>• Modération automatique des contenus</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminSettingsPage;
